import { callExternalApi, CallResult } from '../callExternalApi';
import {
  AdviseEngagement,
  CurrentSupplierChange,
  NewCurrentSupplier,
  SupplierInvoiceFileLinks,
  SupplierLineItemsReplacement,
  SupplierSelectionEntry,
  SupplierSelectionSaved
} from '../../types/advise';

export const updateAdviseSupplierSelection = async (
  id: string,
  suppliers: SupplierSelectionEntry[]
): Promise<CallResult<SupplierSelectionSaved>> => {
  return callExternalApi<SupplierSelectionSaved>({
    url: `api/advise/engagements/${id}/supplier_selection`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { suppliers }
  });
};

export const updateAdviseCurrentSuppliersSkip = async (
  id: string,
  skipped: boolean
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}/current_suppliers/skip`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { skipped }
  });
};

const currentSupplierUrl = (id: string, supplierId: string) =>
  `api/advise/engagements/${id}/current_suppliers/suppliers/${supplierId}`;

export const createAdviseCurrentSupplier = async (
  id: string,
  supplier: NewCurrentSupplier
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}/current_suppliers/suppliers`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: supplier
  });
};

export const deleteAdviseCurrentSupplier = async (
  id: string,
  supplierId: string
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: currentSupplierUrl(id, supplierId),
    method: 'DELETE',
    headers: { 'content-type': 'application/json' }
  });
};

export const uploadAdviseSupplierInvoice = async (
  id: string,
  supplierId: string,
  file: File
): Promise<CallResult<AdviseEngagement>> => {
  const formData = new FormData();
  formData.append('file', file);
  return callExternalApi<AdviseEngagement>({
    url: `${currentSupplierUrl(id, supplierId)}/invoices`,
    method: 'POST',
    headers: { 'content-type': 'multipart/form-data' },
    data: formData
  });
};

export const deleteAdviseSupplierInvoice = async (
  id: string,
  supplierId: string,
  invoiceId: string
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `${currentSupplierUrl(id, supplierId)}/invoices/${invoiceId}`,
    method: 'DELETE',
    headers: { 'content-type': 'application/json' }
  });
};

export const updateAdviseCurrentSupplier = async (
  id: string,
  supplierId: string,
  change: CurrentSupplierChange
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: currentSupplierUrl(id, supplierId),
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: change
  });
};

const replaceLineItems = (url: string, lines: SupplierLineItemsReplacement) =>
  callExternalApi<AdviseEngagement>({
    url,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: lines
  });

export const updateAdviseInvoiceLineItems = async (
  id: string,
  supplierId: string,
  invoiceId: string,
  lines: SupplierLineItemsReplacement
): Promise<CallResult<AdviseEngagement>> =>
  replaceLineItems(`${currentSupplierUrl(id, supplierId)}/invoices/${invoiceId}/line_items`, lines);

// upstack_api refuses this with a 422 once the supplier has an invoice, whose own line items are replaced instead.
export const updateAdviseSupplierLineItems = async (
  id: string,
  supplierId: string,
  lines: SupplierLineItemsReplacement
): Promise<CallResult<AdviseEngagement>> => replaceLineItems(`${currentSupplierUrl(id, supplierId)}/line_items`, lines);

export const fetchAdviseInvoiceFile = async (
  id: string,
  supplierId: string,
  invoiceId: string
): Promise<CallResult<SupplierInvoiceFileLinks>> => {
  return callExternalApi<SupplierInvoiceFileLinks>({
    url: `${currentSupplierUrl(id, supplierId)}/invoices/${invoiceId}/file`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};
